import {
  Bell,
  Building,
  ChevronDown,
  FileText,
  Home,
  MessageSquarePlus,
  PanelLeft,
} from 'lucide-react';
import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { WithCapability } from '@components/auth/WithCapability';
import FeedbackDialog from '@components/feedback/FeedbackDialog';
import { NotificationsInbox } from '@components/notifications/NotificationsInbox';
import WhatsNewBadge from '@components/whatsnew/WhatsNewBadge';
import WhatsNewDialog from '@components/whatsnew/WhatsNewDialog';
import { useCapabilities } from '@hooks/useCapabilities';
import { cn } from '@lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@ui/collapsible';
import {
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@ui/sidebar';

export function NavOfferApp() {
  const location = useLocation();
  const [feedbackDialogOpen, setFeedbackDialogOpen] = React.useState(false);
  const [whatsNewDialogOpen, setWhatsNewDialogOpen] = React.useState(false);
  const { toggleSidebar } = useSidebar();
  const {
    canViewOffers,
    canCreateOffers,
    canViewCompanySettings,
    canUpdateCompanySettings,
    canManageUsers,
    canManageSSO,
    canViewValuations,
    canUpdateValuations: _canUpdateValuations,
    canSubmitFeedback: _canSubmitFeedback,
    canViewWhatsNew: _canViewWhatsNew,
    canViewAuditEvents,
    hasCapability,
  } = useCapabilities();

  // Build company menu items conditionally based on capabilities
  const companyMenuItems = [
    // Company Settings - show if user can view or update company settings
    ...(canViewCompanySettings() || canUpdateCompanySettings()
      ? [
          {
            title: 'Company Settings',
            url: '/company/settings',
            capability: 'company_settings.view',
          },
        ]
      : []),
    // Company Branding - show if user can view or update company settings
    ...(canViewCompanySettings() || canUpdateCompanySettings()
      ? [
          {
            title: 'Company Branding',
            url: '/company/branding',
            capability: 'company_settings.view',
          },
        ]
      : []),
    // Offer Settings - show if user can view or update company settings
    ...(canViewCompanySettings() || canUpdateCompanySettings()
      ? [
          {
            title: 'Offer Settings',
            url: '/company/offer-settings',
            capability: 'company_settings.view',
          },
        ]
      : []),
    // Valuation & Comparables - show if user can view valuations
    ...(canViewValuations()
      ? [
          {
            title: 'Valuation & Comparables',
            url: '/company/valuation-comparables',
            capability: 'valuations.view',
          },
        ]
      : []),
    // Manage Users - show only if user can manage users
    ...(canManageUsers()
      ? [
          {
            title: 'Manage Users',
            url: '/company/users',
            capability: 'company_settings.manage_users',
          },
        ]
      : []),
    // Benefit Templates - show if user can view company settings
    ...(canViewCompanySettings()
      ? [
          {
            title: 'Benefit Templates',
            url: '/company/benefit-templates',
            capability: 'company_settings.view',
          },
        ]
      : []),
    // Document Templates - show if user can view company settings
    ...(canViewCompanySettings()
      ? [
          {
            title: 'Document Templates',
            url: '/company/document-templates',
            capability: 'company_settings.view',
          },
        ]
      : []),
    // Salary & Equity Bands - show if user can view company settings
    ...(canViewCompanySettings()
      ? [
          {
            title: 'Salary & Equity Bands',
            url: '/company/salary-equity-bands',
            capability: 'company_settings.view',
          },
        ]
      : []),
    // Internal Equity - show if user can view company settings
    ...(canViewCompanySettings()
      ? [
          {
            title: 'Internal Equity',
            url: '/company/internal-equity',
            capability: 'company_settings.view',
          },
        ]
      : []),
    // Integrations - show if user can view company settings
    ...(canViewCompanySettings()
      ? [
          {
            title: 'Integrations',
            url: '/company/integrations',
            capability: 'company_settings.view',
          },
        ]
      : []),
    // Single Sign On - show only if user can manage SSO
    ...(canManageSSO()
      ? [
          {
            title: 'Single Sign On',
            url: '/company/sso',
            capability: 'company_settings.manage_sso',
          },
        ]
      : []),
    // Billing - gated purely by capability (Admin-only via billing.manage)
    ...(hasCapability('billing.manage')
      ? [
          {
            title: 'Billing',
            url: '/company/billing',
            capability: 'billing.manage',
          },
        ]
      : []),
    // Audit Log - show only if user can view audit events
    ...(canViewAuditEvents()
      ? [
          {
            title: 'Audit Log',
            url: '/company/audit-log',
            capability: 'audit.view',
          },
        ]
      : []),
  ];

  // Build offers menu items conditionally based on capabilities
  const offerMenuItems = [
    // Create Offer - show only if user can create offers
    ...(canCreateOffers()
      ? [
          {
            title: 'Create Offer',
            url: '/offers/create',
            capability: 'offers.create',
          },
        ]
      : []),
    // View Offers - show if user can view offers
    ...(canViewOffers()
      ? [
          {
            title: 'View Offers',
            url: '/dashboard',
            capability: 'offers.view',
          },
        ]
      : []),
    // All Offers - show only for users with cross-company access
    ...(hasCapability('agency.cross_company')
      ? [
          {
            title: 'All Offers',
            url: '/offers/all',
            capability: 'offers.view',
          },
        ]
      : []),
  ];

  const mainNavItems = [
    {
      title: 'Dashboard',
      url: '/dashboard',
      icon: Home,
    },
    // Offers section - show only if user has any offer capabilities
    ...(canViewOffers() || canCreateOffers()
      ? [
          {
            title: 'Offers',
            url: '#',
            icon: FileText,
            items: offerMenuItems,
          },
        ]
      : []),
    // Company section - show only if user has any company capabilities
    ...(companyMenuItems.length > 0
      ? [
          {
            title: 'Company',
            url: '#',
            icon: Building,
            items: companyMenuItems,
          },
        ]
      : []),
  ];

  return (
    <div data-testid="nav-offer-app-container">
      {mainNavItems.map(item => (
        <React.Fragment key={item.title}>
          {item.items ? (
            <Collapsible data-testid={`nav-item-${item.title.toLowerCase()}`}>
              <CollapsibleTrigger asChild>
                <SidebarMenuButton data-testid={`nav-button-${item.title.toLowerCase()}`}>
                  {item.icon && (
                    <item.icon
                      className="h-4 w-4"
                      data-testid={`nav-icon-${item.title.toLowerCase()}`}
                    />
                  )}
                  <span data-testid={`nav-text-${item.title.toLowerCase()}`}>{item.title}</span>
                  <ChevronDown
                    className="h-4 w-4 shrink-0 transition-transform duration-200"
                    data-testid={`nav-chevron-${item.title.toLowerCase()}`}
                  />
                </SidebarMenuButton>
              </CollapsibleTrigger>
              <CollapsibleContent data-testid={`nav-content-${item.title.toLowerCase()}`}>
                <SidebarMenuSub data-testid={`nav-submenu-${item.title.toLowerCase()}`}>
                  {item.items.map(subItem => (
                    <SidebarMenuSubItem
                      key={subItem.title}
                      data-testid={`nav-submenu-item-${subItem.title.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <Link
                        to={subItem.url}
                        className="w-full"
                        data-testid={`nav-link-${subItem.title.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <SidebarMenuSubButton
                          className={cn('w-full', location.pathname === subItem.url && 'bg-muted')}
                          data-testid={`nav-subitem-${subItem.title.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <span
                            data-testid={`nav-subitem-text-${subItem.title.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {subItem.title}
                          </span>
                        </SidebarMenuSubButton>
                      </Link>
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              </CollapsibleContent>
            </Collapsible>
          ) : (
            <Link
              to={item.url}
              className="w-full"
              data-testid={`nav-link-${item.title.toLowerCase()}`}
            >
              <SidebarMenuButton
                className={cn('w-full', location.pathname === item.url && 'bg-muted')}
                data-testid={`nav-button-${item.title.toLowerCase()}`}
              >
                {item.icon && (
                  <item.icon
                    className="h-4 w-4"
                    data-testid={`nav-icon-${item.title.toLowerCase()}`}
                  />
                )}
                <span data-testid={`nav-text-${item.title.toLowerCase()}`}>{item.title}</span>
              </SidebarMenuButton>
            </Link>
          )}
        </React.Fragment>
      ))}

      {/* Notifications Inbox - show only if user can view offers */}
      <WithCapability capability="offers.view">
        <NotificationsInbox />
      </WithCapability>

      {/* What's New Button - show only if user can view what's new */}
      <WithCapability capability="whatsnew.view">
        <SidebarMenuButton
          onClick={() => setWhatsNewDialogOpen(true)}
          className="w-full relative"
          data-testid="nav-button-whatsnew"
        >
          <Bell className="h-4 w-4" data-testid="nav-icon-whatsnew" />
          <span data-testid="nav-text-whatsnew">What's New</span>
          <WhatsNewBadge className="absolute right-8" />
        </SidebarMenuButton>

        {/* What's New Dialog */}
        <WhatsNewDialog open={whatsNewDialogOpen} onOpenChange={setWhatsNewDialogOpen} />
      </WithCapability>

      {/* Submit Feedback Button - show only if user can submit feedback */}
      <WithCapability capability="feedback.submit">
        <SidebarMenuButton
          onClick={() => setFeedbackDialogOpen(true)}
          className="w-full"
          data-testid="nav-button-feedback"
        >
          <MessageSquarePlus className="h-4 w-4" data-testid="nav-icon-feedback" />
          <span data-testid="nav-text-feedback">Submit Feedback</span>
        </SidebarMenuButton>

        {/* Feedback Dialog */}
        <FeedbackDialog
          open={feedbackDialogOpen}
          onOpenChange={setFeedbackDialogOpen}
          data-testid="feedback-dialog"
        />
      </WithCapability>

      <SidebarMenuButton
        onClick={toggleSidebar}
        className="w-full"
        data-testid="nav-button-toggle-sidebar"
      >
        <PanelLeft className="h-4 w-4" data-testid="nav-icon-toggle-sidebar" />
        <span data-testid="nav-text-toggle-sidebar">Toggle Sidebar</span>
      </SidebarMenuButton>
    </div>
  );
}
